import { useState, useRef, useEffect } from "react";
import { getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";
import { CoachBadge, Disclaimer } from "@/components/program/shared";

const SUGGESTIONS = ["What workout do I have today?", "Swap my breakfast", "I only have 20 minutes today", "How is my progress?"];

export const Coach = ({ accessId }) => {
  const [messages, setMessages] = useState([{ role: "coach", text: "Hi, I'm Chris. I've got your full program in front of me — ask me anything about today, your meals, your training or how it's all going." }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(`coach-${accessId}-${Date.now()}`);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    setMessages((m) => [...m, { role: "user", text }, { role: "coach", text: "" }]);
    setInput(""); setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${accessId}/coach`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: sessionId.current, text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + ev.delta }; return c; });
        }
      }
    } catch { setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "coach", text: "I couldn't reach the coaching desk just now — try again in a moment." }; return c; }); }
    setStreaming(false);
  };

  return (
    <div className="pt-6 flex flex-col h-[calc(100vh-160px)]" data-testid="program-coach">
      <CoachBadge />
      <div className="flex-1 overflow-y-auto mt-4 space-y-3 pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-cyan-400/15 text-cyan-100" : "glass text-slate-200"}`} data-testid={`coach-msg-${m.role}`}>
              {m.text || <span className="animate-pulse text-slate-500">…</span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex flex-wrap gap-2 my-3">
        {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full glass text-[11px] text-slate-300 hover:text-cyan-300" data-testid="coach-suggestion">{s}</button>)}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(input)} placeholder="Ask your coach…" className="bg-black/40 border-white/10" data-testid="coach-input" />
        <Button onClick={() => send(input)} disabled={streaming} className="btn-primary rounded-lg" data-testid="coach-send"><Send size={15} /></Button>
      </div>
      <Disclaimer className="mt-3" />
    </div>
  );
};
